import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  CaretDown, CaretRight, PencilSimple, ArrowsClockwise,
  TrashSimple, PlusCircle, Clock, PaperPlaneTilt, CheckCircle
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const STATUS_CONFIG = {
  brouillon: { label: 'Brouillon', icon: Clock, color: 'text-gray-500', badge: 'bg-gray-100 text-gray-700', dot: 'bg-gray-400' },
  en_cours: { label: 'En cours', icon: PaperPlaneTilt, color: 'text-blue-500', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-400' },
  valide: { label: 'Valide', icon: CheckCircle, color: 'text-emerald-500', badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-400' },
  soumis: { label: 'En cours', icon: PaperPlaneTilt, color: 'text-blue-500', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-400' },
  approuve: { label: 'Valide', icon: CheckCircle, color: 'text-emerald-500', badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-400' },
};

function _isMainSection(stripped) {
  return /^\d+\.\s+[A-Z]/.test(stripped) && stripped === stripped.toUpperCase() && stripped.length > 5;
}

function _isDocTitle(stripped) {
  return !stripped.startsWith('====') && stripped === stripped.toUpperCase() && stripped.length > 25 && !/^\d/.test(stripped);
}

function _isSpecialHeader(stripped) {
  return stripped.startsWith('DONNEES DE REFERENCE') || stripped.startsWith('EN-TETE') || stripped.startsWith('IDENTIFICATION');
}

function _isSpecialHeaderSep(stripped, lines, i) {
  if (!stripped.startsWith('======') || i + 1 >= lines.length) return false;
  const next = lines[i + 1].trim();
  return next.startsWith('DONNEES') || next.startsWith('EN-TETE') || next.startsWith('IDENTIFICATION');
}

function _isAppendixOrSignature(stripped) {
  return stripped === 'ANNEXES' || stripped.startsWith('ANNEXE') || stripped === 'SIGNATURES' || stripped === 'VISA' || stripped.startsWith('VISA DU');
}

export function parseSections(text) {
  if (!text) return [];
  const lines = text.split('\n');
  const sections = [];
  let current = null;
  let headerBuffer = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const stripped = line.trim();
    if (_isMainSection(stripped)) {
      if (current) sections.push(current);
      current = { title: stripped, content: '', isHeader: false };
    } else if (_isDocTitle(stripped) && sections.length === 0 && !current) {
      headerBuffer.push(line);
    } else if (stripped.startsWith('======') && !current && headerBuffer.length > 0) {
      headerBuffer.push(line);
    } else if (_isSpecialHeader(stripped) || _isSpecialHeaderSep(stripped, lines, i)) {
      if (current) sections.push(current);
      const nextTitle = (i + 1 < lines.length) ? lines[i + 1].trim() : stripped;
      current = { title: nextTitle.startsWith('====') ? stripped : nextTitle, content: '', isHeader: true };
    } else if (_isAppendixOrSignature(stripped)) {
      if (current) sections.push(current);
      current = { title: stripped, content: '', isHeader: false };
    } else {
      if (current) {
        current.content += line + '\n';
      } else {
        headerBuffer.push(line);
      }
    }
  }
  if (current) sections.push(current);
  if (headerBuffer.length > 0) {
    const headerText = headerBuffer.join('\n').trim();
    if (headerText) sections.unshift({ title: 'EN-TETE DU DOCUMENT', content: headerText + '\n', isHeader: true });
  }
  return sections.map(s => ({
    ...s,
    content: s.content.replace(/^[=\s]+\n/g, '').replace(/\n[=\s]+$/g, '').trim()
  }));
}

export function sectionsToText(sections) {
  let text = '';
  for (const s of sections) {
    if (s.isHeader && s.title === 'EN-TETE DU DOCUMENT') {
      text += s.content + '\n\n';
      continue;
    }
    text += '======================================================================\n';
    text += s.title + '\n';
    text += '======================================================================\n\n';
    text += s.content + '\n\n';
  }
  return text.trim();
}

export function SectionBlock({ section, index, onUpdate, onDelete, onRegenerate, regeneratingIdx, isLocked }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [localContent, setLocalContent] = useState(section.content);
  const isRegenerating = regeneratingIdx === index;
  const saveEdit = () => { onUpdate(index, localContent); setEditing(false); };
  const cancelEdit = () => { setLocalContent(section.content); setEditing(false); };
  useEffect(() => { setLocalContent(section.content); }, [section.content]);

  return (
    <div className={`border rounded-lg overflow-hidden transition-all ${expanded ? 'border-[#FF6B00]/30 shadow-sm' : 'border-gray-200'}`}
      data-testid={`section-block-${index}`}>
      <button type="button" onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 sm:px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
        data-testid={`section-toggle-${index}`}>
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {expanded ? <CaretDown size={16} className="text-[#FF6B00] flex-shrink-0" /> : <CaretRight size={16} className="text-gray-400 flex-shrink-0" />}
          <span className={`font-semibold text-xs sm:text-sm truncate ${expanded ? 'text-[#FF6B00]' : 'text-[#111827]'}`}>
            {section.title}
          </span>
        </div>
        {!section.isHeader && !isLocked && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <span onClick={(e) => { e.stopPropagation(); onRegenerate(index); }}
              className={`p-1.5 rounded hover:bg-orange-50 cursor-pointer ${isRegenerating ? 'animate-spin' : ''}`}
              data-testid={`section-regen-${index}`}>
              <ArrowsClockwise size={16} className="text-[#FF6B00]" />
            </span>
            <span onClick={(e) => { e.stopPropagation(); setExpanded(true); setEditing(true); }}
              className="p-1.5 rounded hover:bg-blue-50 cursor-pointer">
              <PencilSimple size={16} className="text-blue-600" />
            </span>
            <span onClick={(e) => { e.stopPropagation(); onDelete(index); }}
              className="p-1.5 rounded hover:bg-red-50 cursor-pointer">
              <TrashSimple size={16} className="text-red-500" />
            </span>
          </div>
        )}
      </button>
      {expanded && (
        <div className="px-3 sm:px-4 pb-4 bg-gray-50/50">
          {isRegenerating && (
            <div className="flex items-center gap-2 py-3 text-sm text-[#FF6B00]">
              <Loader2 className="h-4 w-4 animate-spin" /> Regeneration en cours...
            </div>
          )}
          {editing && !isLocked ? (
            <div className="space-y-2 pt-2">
              <Textarea value={localContent} onChange={(e) => setLocalContent(e.target.value)}
                rows={Math.min(20, Math.max(6, localContent.split('\n').length + 2))}
                className="font-mono text-xs sm:text-sm leading-relaxed" data-testid={`section-edit-${index}`} />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={cancelEdit} className="h-9">Annuler</Button>
                <Button size="sm" onClick={saveEdit} className="h-9 bg-[#FF6B00] hover:bg-orange-700" data-testid={`section-save-${index}`}>
                  Enregistrér
                </Button>
              </div>
            </div>
          ) : (
            <pre className="whitespace-pre-wrap text-xs sm:text-sm text-[#374151] font-mono leading-relaxed pt-2 max-h-[300px] overflow-y-auto">
              {section.content || '(Section vide)'}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

export function SectionEditor({ sections, setSections, chantierId, typeDoc, summary, isLocked }) {
  const [regeneratingIdx, setRegeneratingIdx] = useState(null);
  const handleUpdate = (idx, content) => {
    const updated = [...sections];
    updated[idx] = { ...updated[idx], content };
    setSections(updated);
  };
  const handleDelete = (idx) => {
    setSections(sections.filter((_, i) => i !== idx));
  };
  const handleRegenerate = async (idx) => {
    if (isLocked) return;
    setRegeneratingIdx(idx);
    try {
      const res = await import('axios').then(m => m.default.post(
        `${API}/csps/documents/regenerate-section`,
        { chantier_id: chantierId, type_document: typeDoc, section_title: sections[idx].title, section_content: sections[idx].content },
        { withCredentials: true }
      ));
      handleUpdate(idx, res.data.section_content);
    } catch (_err) { toast.error('Erreur regeneration section'); }
    finally { setRegeneratingIdx(null); }
  };
  const addSection = () => {
    setSections([...sections, { title: `NOUVELLE SECTION`, content: '[A completer]', isHeader: false }]);
  };

  return (
    <div className="space-y-2" data-testid="section-editor">
      {summary && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3" data-testid="editor-summary">
          <p className="text-xs font-bold text-orange-800 uppercase mb-1.5">Donnees de reference</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-orange-700">
            <span>MO : {summary.mo}</span>
            <span>MOE : {summary.moe || '-'}</span>
            <span>CSPS : {summary.csps || '-'}</span>
            <span>Niveau : {summary.niveau_csps}</span>
            <span>Phase : {(summary.phase || '').charAt(0).toUpperCase() + (summary.phase || '').slice(1)}</span>
            {summary.nature_travaux && <span>Nature : {summary.nature_travaux}</span>}
            {summary.duree && <span>Duree : {summary.duree}</span>}
            {summary.numéro_operation && <span>N. op. : {summary.numéro_operation}</span>}
            {summary.reference_dossier && <span>Ref. : {summary.reference_dossier}</span>}
            <span className="col-span-2">Entreprises : {(summary.entreprises || []).join(', ') || 'Aucune'}</span>
            {summary.risques && <span className="col-span-2">Risques : {summary.risques.substring(0, 80)}{summary.risques.length > 80 ? '...' : ''}</span>}
          </div>
        </div>
      )}
      {sections.map((s, i) => (
        <SectionBlock key={s.id || `section-${i}`} section={s} index={i} onUpdate={handleUpdate} onDelete={handleDelete}
          onRegenerate={handleRegenerate} regeneratingIdx={regeneratingIdx} isLocked={isLocked} />
      ))}
      {!isLocked && (
        <Button variant="outline" size="sm" onClick={addSection} className="w-full h-10 border-dashed border-gray-300 text-gray-500"
          data-testid="add-section-btn">
          <PlusCircle size={16} className="mr-2" /> Ajouter une section
        </Button>
      )}
    </div>
  );
}

export function DocBanner({ name, chantierName, version, status, date }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.brouillon;
  return (
    <div className="bg-gradient-to-r from-[#1A1A2E] to-[#16213E] text-white rounded-lg p-3 sm:p-4" data-testid="doc-banner">
      <h2 className="text-base sm:text-lg font-bold truncate">{name}</h2>
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1.5 text-xs sm:text-sm text-gray-300">
        <span>{chantierName}</span>
        <span className="opacity-50">|</span>
        <span>V{version}</span>
        <span className="opacity-50">|</span>
        <span className={`px-2 py-0.5 rounded text-xs font-bold ${cfg.badge}`}>{cfg.label}</span>
        {date && <span className="opacity-50">| {new Date(date).toLocaleDateString('fr-FR')}</span>}
      </div>
    </div>
  );
}

export function getChantierSummary(chantier) {
  if (!chantier) return null;
  return {
    mo: chantier.client_name, moe: chantier.maitre_oeuvre, csps: chantier.coordonnateur_sps,
    niveau_csps: chantier.niveau_csps, phase: chantier.phase_chantier, entreprises: chantier.entreprises,
    nature_travaux: chantier.nature_travaux, duree: chantier.duree_previsionnelle,
    acces: chantier.acces_chantier, risques: chantier.risques_principaux,
    numéro_operation: chantier.numéro_operation, reference_dossier: chantier.reference_dossier
  };
}
